import { Injectable, signal } from '@angular/core';

/**
 * Toast notification type — matches InlineAlert types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Single toast notification
 */
export interface Toast {
  id: number;
  type: ToastType;
  title: string;
  message: string;
}

/**
 * ToastService
 *
 * Global notification service for displaying toast messages app-wide.
 * Used by the error interceptor to surface backend error messages
 * and by any component that needs to show transient notifications.
 *
 * Toasts auto-dismiss after a configurable duration.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private nextId = 0;
  private readonly defaultDuration = 6000; // 6 seconds

  /** Active toasts (reactive signal for template binding) */
  readonly toasts = signal<Toast[]>([]);

  /**
   * Show an error toast with the backend error message
   */
  showError(message: string, title = 'Error'): void {
    this.addToast('error', title, message);
  }

  /**
   * Show a success toast
   */
  showSuccess(message: string, title = 'Success'): void {
    this.addToast('success', title, message);
  }

  /**
   * Show a warning toast
   */
  showWarning(message: string, title = 'Warning'): void {
    this.addToast('warning', title, message);
  }

  /**
   * Show an info toast
   */
  showInfo(message: string, title = 'Info'): void {
    this.addToast('info', title, message);
  }

  /**
   * Dismiss a specific toast by id
   */
  dismiss(id: number): void {
    this.toasts.update(list => list.filter(t => t.id !== id));
  }

  /**
   * Add a toast and schedule auto-dismiss
   */
  private addToast(
    type: ToastType,
    title: string,
    message: string,
    duration = this.defaultDuration
  ): void {
    const id = this.nextId++;
    const toast: Toast = { id, type, title, message };

    this.toasts.update(list => [...list, toast]);

    // Auto-dismiss
    setTimeout(() => this.dismiss(id), duration);
  }
}
